import React, { useRef, useEffect, useState } from 'react';
import { View, Button, ProgressBar, Text, TextInput, NumberInput, TextArea } from '@smarterservices/smarter-ui';
import HlsViewer from './index';
import Hls from 'hls.js';

const HlsViewerExample = () => {
  // Example region bounds as provided
  const regionBounds = {
    id: 'f05b2405198a6ea6b252a35e303b151eacc97cd99c23504151a236ae180c6e84',
    top: 0,
    left: 437.5,
    width: 405,
    height: 720,
    changeDate: '2025-10-07T18:25:43.604',
    sessionChangeTime: 169,
    aspectRatio: 0.625,
  };

  // HLS URL as provided
  const hlsUrl =
    'https://playback.dev.proctoring.media/v1/tok_BgABr3eOzAFJR7gBW_DCxGHiHn3IJIswHczmleKMDuE/installs/AI62678ae6437b48de9763ad0236c60a51/exams/EX9c83568a350c46cd9dfb1fe592417501/sessions/ESdeec773aed6b4a92a1543d6cdd4d4afc/playlist.m3u8';

  // Create a reference for the main video element
  const mainVideoRef = useRef(null);
  const hlsViewerRef = useRef(null);
  const streamRef = useRef(null);
  const [isMainVideoReady, setIsMainVideoReady] = useState(false);
  const [eventLog, setEventLog] = useState([]);

  // Playback control state
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const [sourceVideoWidth, setSourceVideoWidth] = useState(1920);
  const [sourceVideoHeight, setSourceVideoHeight] = useState(1080);
  const [isReadyLogged, setIsReadyLogged] = useState(false);

  // Adjustable properties for the interactive example
  const [adjustableWidth, setAdjustableWidth] = useState(350);
  const [adjustableHeight, setAdjustableHeight] = useState(350);
  const [adjustableRegionBounds, setAdjustableRegionBounds] = useState({
    id: regionBounds.id,
    top: regionBounds.top,
    left: regionBounds.left,
    width: regionBounds.width,
    height: regionBounds.height,
    changeDate: regionBounds.changeDate,
    sessionChangeTime: regionBounds.sessionChangeTime,
    aspectRatio: regionBounds.aspectRatio,
  });

  // Helper function to update region bounds
  const updateRegionBound = (property, value) => {
    const numValue = parseFloat(value) || 0;
    setAdjustableRegionBounds(prev => ({
      ...prev,
      [property]: numValue,
      aspectRatio: property === 'width' || property === 'height'
        ? (property === 'width' ? numValue / prev.height : prev.width / numValue)
        : prev.aspectRatio
    }));
  };

  // JSON Track Navigation state
  const [jsonInput, setJsonInput] = useState('');
  const [parsedTracks, setParsedTracks] = useState(null);
  const [jsonError, setJsonError] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(null);

  // Parse JSON and extract tracks
  const handleJsonSubmit = () => {
    try {
      const data = JSON.parse(jsonInput);
      if (data.tracks) {
        setParsedTracks(data.tracks);
        setJsonError('');
        setEventLog(prev => [...prev, `Loaded ${Object.keys(data.tracks).length} tracks at ${new Date().toLocaleTimeString()}`]);
      } else {
        setJsonError('Invalid JSON: No tracks found');
      }
    } catch (error) {
      setJsonError(`JSON Parse Error: ${error.message}`);
      setParsedTracks(null);
    }
  };

  // Handle position click - seek video and update region
  const handlePositionClick = (position, trackId, trackInfo) => {
    if (position.sessionChangeTime !== undefined) {
      // Seek to the session change time (convert to seconds if needed)
      const seekTime = Math.max(0, position.sessionChangeTime);

      console.log(`Seeking to: ${seekTime} seconds`);

      // Simple direct approach - just use the main video ref
      if (mainVideoRef.current) {
        console.log('Current video time before seek:', mainVideoRef.current.currentTime);
        mainVideoRef.current.currentTime = seekTime;
        console.log('Video time after seek:', mainVideoRef.current.currentTime);

        // Force the video to update by triggering a small play/pause if needed
        if (mainVideoRef.current.paused) {
          console.log('Video is paused, not forcing play');
        } else {
          console.log('Video is playing, seek should work immediately');
        }
      } else {
        console.error('Main video ref not available!');
      }

      // Update our state
      setCurrentTime(seekTime);

      // Update region bounds to this position
      const newRegionBounds = {
        id: position.id,
        top: position.top || 0,
        left: position.left || 0,
        width: position.width || 1920,
        height: position.height || 1080,
        changeDate: position.changeDate,
        sessionChangeTime: position.sessionChangeTime,
        aspectRatio: position.width && position.height ? position.width / position.height : 1
      };

      setAdjustableRegionBounds(newRegionBounds);
      setSelectedPosition({ trackId, position });

      // Don't change width/height - those are viewport dimensions, not region dimensions

      // Log the action
      setEventLog(prev => [
        ...prev,
        `Jumped to ${trackInfo.source} track at ${formatTime(seekTime)} - Region: ${position.left},${position.top} ${position.width}x${position.height}`
      ]);

      console.log(`Navigated to position:`, { trackId, position, seekTime, videoCurrentTime: mainVideoRef.current?.currentTime });
    }
  };

  // Clear JSON data
  const handleClearJson = () => {
    setJsonInput('');
    setParsedTracks(null);
    setJsonError('');
    setSelectedPosition(null);
  };

  // Event handlers
  const handleReady = (data) => {
    // Only log the first ready event to prevent spam
    if (!isReadyLogged) {
      console.log('Video ready:', data);
      setEventLog((prev) => [
        ...prev,
        `Ready: ${new Date().toLocaleTimeString()}`,
      ]);
      setIsReadyLogged(true);
    }
  };

  const handlePlay = () => {
    console.log('Video playing');
    setEventLog((prev) => [
      ...prev,
      `Play: ${new Date().toLocaleTimeString()}`,
    ]);
  };

  const handlePause = () => {
    console.log('Video paused');
    setEventLog((prev) => [
      ...prev,
      `Pause: ${new Date().toLocaleTimeString()}`,
    ]);
  };

  const handleError = (error) => {
    console.error('Video error:', error);
    setEventLog((prev) => [
      ...prev,
      `Error: ${error?.message || 'Unknown error'}`,
    ]);
  };

  const handleTimeUpdate = (e) => {
    const time = e.target.currentTime;
    setCurrentTime(time);

    // Don't log every time update to avoid flooding the log
    if (Math.floor(time) % 5 === 0) {
      // Log every 5 seconds
      setEventLog((prev) => {
        const timeMsg = `Time: ${Math.floor(time)}s`;
        // Avoid duplicate consecutive entries
        if (prev.length > 0 && prev[prev.length - 1].includes(timeMsg)) {
          return prev;
        }
        return [...prev, timeMsg];
      });
    }
  };

  // Format time in MM:SS format
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return '0:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Playback control functions
  const handlePlayPause = () => {
    // Use HlsViewer's methods if available, otherwise fallback to direct video control
    if (hlsViewerRef.current) {
      if (isPlaying) {
        hlsViewerRef.current.pause();
      } else {
        hlsViewerRef.current.play();
      }
    } else if (mainVideoRef.current) {
      if (isPlaying) {
        mainVideoRef.current.pause();
      } else {
        mainVideoRef.current.play();
      }
    }
  };

  const handleRestart = () => {
    if (mainVideoRef.current) {
      mainVideoRef.current.currentTime = 0;
      mainVideoRef.current.play();
    }
  };

  const handleSeek = (e) => {
    if (mainVideoRef.current && duration > 0) {
      // Always get the rect from the progress bar container, not the clicked element
      const progressBar = e.currentTarget; // Use currentTarget instead of target
      const rect = progressBar.getBoundingClientRect();
      const position = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const seekTime = position * duration;

      console.log(`Seeking to: ${formatTime(seekTime)} (${(position * 100).toFixed(1)}%)`);

      mainVideoRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);

      // Add to event log
      setEventLog((prev) => [
        ...prev,
        `Seek: ${formatTime(seekTime)} at ${new Date().toLocaleTimeString()}`,
      ]);
    }
  };

  // Initialize the main video with HLS
  useEffect(() => {
    const video = mainVideoRef.current;
    if (!video) return;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsMainVideoReady(true);
        setDuration(video.duration || 0);
        video.play().catch((e) => console.log('Error playing main video:', e));
      });

      // Add event listeners for playback state
      video.addEventListener('play', () => setIsPlaying(true));
      video.addEventListener('pause', () => setIsPlaying(false));
      video.addEventListener('loadedmetadata', () => {
        setDuration(video.duration || 0);
      });
      video.addEventListener('timeupdate', (e) => {
        setCurrentTime(e.target.currentTime);
      });

      return () => {
        video.removeEventListener('play', () => setIsPlaying(true));
        video.removeEventListener('pause', () => setIsPlaying(false));
        video.removeEventListener('loadedmetadata', () => { });
        video.removeEventListener('timeupdate', () => { });
        hls.destroy();
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // For Safari with native HLS support
      video.src = hlsUrl;
      const handleLoadedMetadata = () => {
        setIsMainVideoReady(true);
        setDuration(video.duration || 0);
        video.play().catch((e) => console.log('Error playing main video:', e));
      };

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      const handleTimeUpdate = (e) => setCurrentTime(e.target.currentTime);

      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('timeupdate', handleTimeUpdate);

      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, []);


  return (
    <View as="div" padding="none" background="#eee">
      <View as="div" display="flex" padding="medium" flexDirection="column">
        <View as="div" margin="small">

          <video
            ref={mainVideoRef}
            style={{
              width: '1px',
              height: '1px',
              position: 'absolute',
              opacity: 0,
            }}
            muted
            playsInline
          />
        </View>


        {/* Main Layout - Side by Side */}
        <View as="div" display="flex" gap="large" flexWrap="wrap">
          {/* Left Column - Controls */}
          <View as="div" minWidth="400px" flex="0 0 auto">
            {/* Editable Properties Panel */}
            <View as="div" margin="0 0 medium 0" padding="medium" background="secondary" borderRadius="medium">
              <h4>Adjust Properties</h4>

              {/* Dimensions Controls */}
              <View as="div" display="flex" gap="medium" margin="0 0 medium 0" flexWrap="wrap">
                <View as="div" minWidth="150px">
                  <Text size="small" weight="bold">Width</Text>
                  <NumberInput
                    value={adjustableWidth}
                    onChange={(e, value) => setAdjustableWidth(parseInt(value) || 0)}
                    renderLabel={() => null}
                    width="100px"
                    interaction="enabled"
                    showArrows={true}
                  />
                </View>
                <View as="div" minWidth="150px">
                  <Text size="small" weight="bold">Height</Text>
                  <NumberInput
                    value={adjustableHeight}
                    onChange={(e, value) => setAdjustableHeight(parseInt(value) || 0)}
                    renderLabel={() => null}
                    width="100px"
                    interaction="enabled"
                    showArrows={true}
                  />
                </View>
              </View>

              {/* Region Bounds Controls */}
              <View as="div">
                <Text size="small" weight="bold" margin="0 0 small 0">Region Bounds</Text>
                <View as="div" display="flex" gap="medium" flexWrap="wrap">
                  <View as="div" minWidth="120px">
                    <Text size="x-small">Top</Text>
                    <NumberInput
                      value={adjustableRegionBounds.top}
                      onChange={(e, value) => updateRegionBound('top', value)}
                      renderLabel={() => null}
                      width="80px"
                      interaction="enabled"
                      showArrows={true}
                    />
                  </View>
                  <View as="div" minWidth="120px">
                    <Text size="x-small">Left</Text>
                    <NumberInput
                      value={adjustableRegionBounds.left}
                      onChange={(e, value) => updateRegionBound('left', value)}
                      renderLabel={() => null}
                      width="80px"
                      interaction="enabled"
                      showArrows={true}
                    />
                  </View>
                  <View as="div" minWidth="120px">
                    <Text size="x-small">Width</Text>
                    <NumberInput
                      value={adjustableRegionBounds.width}
                      onChange={(e, value) => updateRegionBound('width', value)}
                      renderLabel={() => null}
                      width="80px"
                      interaction="enabled"
                      showArrows={true}
                    />
                  </View>
                  <View as="div" minWidth="120px">
                    <Text size="x-small">Height</Text>
                    <NumberInput
                      value={adjustableRegionBounds.height}
                      onChange={(e, value) => updateRegionBound('height', value)}
                      renderLabel={() => null}
                      width="80px"
                      interaction="enabled"
                      showArrows={true}
                    />
                  </View>
                  <View as="div" minWidth="120px">
                    <Text size="x-small">Session Time</Text>
                    <NumberInput
                      value={adjustableRegionBounds.sessionChangeTime}
                      onChange={(e, value) => updateRegionBound('sessionChangeTime', value)}
                      renderLabel={() => null}
                      width="80px"
                      interaction="enabled"
                      showArrows={true}
                    />
                  </View>
                </View>

                {/* Quick Presets */}
                <View as="div" margin="medium 0 0 0">
                  <Text size="small" weight="bold" margin="0 0 small 0">Quick Presets</Text>
                  <View as="div" display="flex" gap="small" flexWrap="wrap">
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => {
                        setAdjustableWidth(600);
                        setAdjustableHeight(100);
                        setAdjustableRegionBounds({
                          ...regionBounds,
                          top: 0,
                          left: 0,
                          width: 1920,
                          height: 1080
                        });
                      }}
                    >
                      Full Screen
                    </Button>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => {
                        setAdjustableWidth(400);
                        setAdjustableHeight(300);
                        setAdjustableRegionBounds({
                          ...regionBounds,
                          top: 100,
                          left: 100,
                          width: 800,
                          height: 600
                        });
                      }}
                    >
                      Center Crop
                    </Button>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => {
                        setAdjustableWidth(800);
                        setAdjustableHeight(200);
                        setAdjustableRegionBounds({
                          ...regionBounds,
                          top: 0,
                          left: 0,
                          width: 1920,
                          height: 400
                        });
                      }}
                    >
                      Top Strip
                    </Button>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => {
                        setAdjustableRegionBounds(regionBounds);
                        setAdjustableWidth(600);
                        setAdjustableHeight(100);
                      }}
                    >
                      Reset to Original
                    </Button>
                  </View>
                </View>
              </View>
            </View>

            {/* JSON Track Navigation */}
            <View as="div" margin="medium 0 0 0" padding="medium" background="secondary" borderRadius="medium">
              <h4>Track Navigation</h4>

              {/* JSON Input */}
              <View as="div" margin="0 0 medium 0">
                <Text size="small" weight="bold" margin="0 0 small 0">Paste Track JSON Data:</Text>
                <textarea
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                  placeholder="Paste your JSON track data here..."
                  style={{
                    width: '100%',
                    height: '120px',
                    minHeight: '120px',
                    maxHeight: '120px',
                    resize: 'none',
                    overflow: 'auto',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    boxSizing: 'border-box'
                  }}
                />

                {/* JSON Controls */}
                <View as="div" display="flex" gap="small" margin="small 0 0 0">
                  <Button size="small" color="primary" onClick={handleJsonSubmit}>
                    Parse Tracks
                  </Button>
                  <Button size="small" color="secondary" onClick={handleClearJson}>
                    Clear
                  </Button>
                </View>

                {/* Error Display */}
                {jsonError && (
                  <View as="div" margin="small 0 0 0" padding="small" background="danger" borderRadius="small">
                    <Text size="small" color="danger-inverse">{jsonError}</Text>
                  </View>
                )}
              </View>

              {/* Tracks List */}
              {parsedTracks && (
                <View as="div">
                  <Text size="small" weight="bold" margin="0 0 small 0">
                    Tracks ({Object.keys(parsedTracks).length}):
                  </Text>

                  {Object.entries(parsedTracks).map(([trackId, trackData]) => {
                    const track = trackData.track;
                    const positions = track.positions || [];

                    return (
                      <View key={trackId} as="div" margin="0 0 medium 0" padding="small" background="primary" borderRadius="small">
                        <Text size="small" weight="bold" color="primary-inverse">
                          {track.source.toUpperCase()} - {track.info.name} ({track.info.width}x{track.info.height})
                        </Text>
                        <Text size="x-small" color="primary-inverse" display="block">
                          Track ID: {trackId}
                        </Text>

                        {/* Positions */}
                        {positions.length > 0 && (
                          <View as="div" margin="small 0 0 0">
                            <Text size="x-small" color="primary-inverse" weight="bold">
                              Positions ({positions.length}):
                            </Text>
                            <View as="div" display="flex" gap="x-small" flexWrap="wrap" margin="x-small 0 0 0">
                              {positions.map((position, index) => {
                                const isSelected = selectedPosition?.trackId === trackId &&
                                  selectedPosition?.position.id === position.id;

                                return (
                                  <Button
                                    key={position.id || index}
                                    size="small"
                                    color={isSelected ? "success" : "secondary"}
                                    onClick={() => handlePositionClick(position, trackId, track)}
                                    style={{ fontSize: '10px', padding: '4px 8px' }}
                                  >
                                    {formatTime(position.sessionChangeTime || 0)}
                                    <br />
                                    <span style={{ fontSize: '8px' }}>
                                      {position.left},{position.top} {position.width}x{position.height}
                                    </span>
                                  </Button>
                                );
                              })}
                            </View>
                          </View>
                        )}

                        {positions.length === 0 && (
                          <Text size="x-small" color="primary-inverse" style={{ fontStyle: 'italic' }}>
                            No positions available
                          </Text>
                        )}
                      </View>
                    );
                  })}
                </View>
              )}
            </View>
          </View>

          {/* Right Column - Live Preview */}
          <View as="div" flex="1 1 auto" minWidth="400px">
            <h3>Live Preview</h3>
            <View as="div" padding="medium" background="primary" borderRadius="medium">
              <HlsViewer
                ref={hlsViewerRef}
                width={adjustableWidth}
                height={adjustableHeight}
                sourceVideoWidth={sourceVideoWidth}
                sourceVideoHeight={sourceVideoHeight}
                hlsRef={mainVideoRef}
                regionBounds={adjustableRegionBounds}
                showDebug={true}
                onReady={handleReady}
                onPlay={handlePlay}
                onPause={handlePause}
                onError={handleError}
                onTimeUpdate={handleTimeUpdate}
              />

              {/* Simple Playback Controls */}
              <View as="div" display="flex" alignItems="center" gap="medium" margin="small 0 0 0">
                <Button
                  onClick={handlePlayPause}
                  color={isPlaying ? 'secondary' : 'primary'}
                  size="small"
                >
                  {isPlaying ? 'Pause' : 'Play'}
                </Button>
                <Text size="small" color="primary-inverse">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>

      {/* Event Log */}
      <View as="div" margin="medium" padding="medium" background="secondary" borderRadius="medium">
        <h3>Event Log</h3>
        <View as="div" maxHeight="200px" style={{ overflowY: 'auto' }}>
          {eventLog.length === 0 ? (
            <Text size="small" color="secondary">No events yet...</Text>
          ) : (
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {eventLog.map((event, index) => (
                <li key={index} style={{ marginBottom: '4px' }}>
                  <Text size="small">{event}</Text>
                </li>
              ))}
            </ul>
          )}
        </View>
      </View>
    </View>
  );
};

export default HlsViewerExample;
